import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { settingsService } from '../../services/settingsService';
import { formatDateTime, formatFileSize } from '../../utils/formatters';
import toast from 'react-hot-toast';
import { CloudArrowDownIcon, ArrowPathIcon, TrashIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';

const BackupRestore = () => {
  const queryClient = useQueryClient();
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);

  const { data, isLoading, refetch } = useQuery(
    'backups',
    () => settingsService.listBackups(),
    { refetchInterval: 30000 }
  );

  const createBackupMutation = useMutation(
    () => settingsService.createBackup(),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('backups');
        toast.success(response.message || 'Backup created successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Backup failed');
      },
      onSettled: () => {
        setIsCreatingBackup(false);
      }
    }
  );

  const handleCreateBackup = () => {
    if (window.confirm('Create a full database backup? This may take a few moments.')) {
      setIsCreatingBackup(true);
      createBackupMutation.mutate();
    }
  };

  const handleDownload = (backupFile) => {
    // Trigger download
    window.location.href = `/api/settings/backup/download/${backupFile}`;
  };

  const backups = data?.data || [];

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold mb-2">Backup & Restore</h2>
          <p className="text-gray-600">Manage database backups and disaster recovery</p>
        </div>
        <button
          onClick={handleCreateBackup}
          disabled={isCreatingBackup}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
        >
          <CloudArrowDownIcon className="h-5 w-5 mr-2" />
          {isCreatingBackup ? 'Creating...' : 'Create Backup'}
        </button>
      </div>

      {/* Backup Info Card */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-yellow-800 mb-2">Automatic Backup Schedule</h3>
        <p className="text-sm text-yellow-700">
          Daily backups are automatically created at 2:00 AM. Backups are stored for 30 days.
          Manual backups can be created at any time.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : backups.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ArrowPathIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p>No backups found</p>
          <button
            onClick={handleCreateBackup}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create First Backup
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Backup File</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {backups.map((backup) => (
                <tr key={backup.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {backup.backup_file?.split('/').pop()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 capitalize">
                      {backup.backup_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatFileSize(backup.file_size)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTime(backup.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      backup.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {backup.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                    <button
                      onClick={() => handleDownload(backup.backup_file)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <DocumentArrowDownIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Restore Section */}
      <div className="mt-8 border-t pt-6">
        <h3 className="font-semibold mb-4">Restore from Backup</h3>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800 mb-3">
            <strong>Warning:</strong> Restoring a backup will overwrite all current data.
            This action cannot be undone. Please ensure you have a current backup before proceeding.
          </p>
          <div className="flex items-center space-x-4">
            <input
              type="file"
              accept=".sql"
              className="text-sm"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file && window.confirm('Are you sure you want to restore from this backup? This will overwrite all current data.')) {
                  toast.info('Restore feature will be implemented soon');
                }
              }}
            />
            <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
              Restore Backup
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupRestore;